/** Recent arrests, pushed in from the jail system. Read-only. */
import { useState } from 'react';
import { api } from '../shared/api.ts';
import { ErrorBox, PageHeader, Spinner, useLoad } from '../shared/ui.tsx';
import { EmptyState, when } from './common.tsx';
import { HookLinkCard } from './HookLink.tsx';

export function ArrestFeedPage() {
  const [days, setDays] = useState(7);
  const [q, setQ] = useState('');
  const r = useLoad(() => api.get<any>(`/api/arrest-feed?days=${days}&q=${encodeURIComponent(q)}`), [days, q]);
  return (
    <div className="ops stack">
      <PageHeader crumb="Arrests" title="Recent arrests" sub={`From the jail system.${r.data?.lastReceivedAt ? ` Last update ${when(r.data.lastReceivedAt)}.` : ''}`} />
      <div className="row gap-sm wrap">
        <div className="seg-ctl" role="tablist" aria-label="How far back">
          {[1, 7, 30, 90].map((d) => (
            <button key={d} type="button" role="tab" aria-selected={days === d} className={days === d ? 'on' : ''} onClick={() => setDays(d)}>
              {d === 1 ? '24 hours' : `${d} days`}
            </button>
          ))}
        </div>
        <input type="search" placeholder="Name, charge, booking #…" value={q} onChange={(e) => setQ(e.target.value)} aria-label="Search arrests" style={{ maxWidth: 260 }} />
      </div>
      {r.error ? (
        <ErrorBox error={r.error} />
      ) : !r.data ? (
        <Spinner />
      ) : !r.data.arrests.length ? (
        <EmptyState icon="arrest" title="No arrests in this range" />
      ) : (
        <div className="card table-wrap">
          <table className="table">
            <thead>
              <tr>
                <th>Name</th>
                <th>Charges</th>
                <th>Arrested</th>
                <th>Officer</th>
                <th>Booking #</th>
              </tr>
            </thead>
            <tbody>
              {r.data.arrests.map((x: any) => (
                <tr key={x.id}>
                  <td>
                    <strong>{x.name}</strong>
                    <div className="small muted">{[x.dob && `DOB ${String(x.dob).slice(0, 10)}`, [x.race, x.sex].filter(Boolean).join('/')].filter(Boolean).join(' · ')}</div>
                  </td>
                  <td className="small wrap">
                    {(x.charges as any[]).map((c, i) => (
                      <div key={i}>
                        {c.statute ? <span className="mono">{c.statute} </span> : null}
                        {c.description}
                      </div>
                    ))}
                  </td>
                  <td>
                    {when(x.arrested_at ?? x.booked_at ?? x.received_at)}
                    {x.facility && <div className="small muted">{x.facility}</div>}
                  </td>
                  <td>{x.officer ?? '—'}</td>
                  <td className="mono">
                    {x.booking_no ?? '—'}
                    {x.case_number && <div className="small muted">Case {x.case_number}</div>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {r.data?.canSetUp && <HookLinkCard title="Connect the jail system" settings="/api/arrest-feed/settings" rotate="/api/arrest-feed/settings/link" what="jail / booking system" jotform={false} detail="Fields: id or bookingNumber, name (or last/first), dob, sex, race, charges (list of text or {statute, description}), arrestedAt, bookedAt, officer, caseNumber, facility. Sending the same id again updates it." />}
    </div>
  );
}
